/** Narrow reads of the frames store; a component re-renders only for its own frame. */
import type { FrameType } from "@/api/spaces";
import type { FrameData } from "@/lib/frame-data";
import { useFramesStore, type FramesState } from "@/stores/frames-store";

export function useFrame(id: string | null | undefined): FrameData | undefined {
  return useFramesStore((state) => (id ? state.framesById[id] : undefined));
}

/** One derived value of one frame; return a primitive or a stable reference. */
export function useFrameField<T>(
  id: string | null | undefined,
  select: (frame: FrameData | undefined) => T,
): T {
  return useFramesStore((state) =>
    select(id ? state.framesById[id] : undefined),
  );
}

/** Every frame in server order (wrap in `useShallow` in hooks). */
export function selectOrderedFrames(
  state: Pick<FramesState, "framesById" | "frameOrder">,
): FrameData[] {
  const frames: FrameData[] = [];
  for (const id of state.frameOrder) {
    const frame = state.framesById[id];
    if (frame) frames.push(frame);
  }
  return frames;
}

/** Frames of the given types, in server order (wrap in `useShallow` in hooks). */
export function selectFramesByType(
  state: Pick<FramesState, "framesById" | "frameOrder">,
  types: ReadonlySet<FrameType> | FrameType,
): FrameData[] {
  const matches =
    typeof types === "string"
      ? (frame: FrameData) => frame.frameType === types
      : (frame: FrameData) => types.has(frame.frameType);
  const frames: FrameData[] = [];
  for (const id of state.frameOrder) {
    const frame = state.framesById[id];
    if (frame && matches(frame)) frames.push(frame);
  }
  return frames;
}

/** Durable layer hide (`metadata.hidden`); focus-mode hiding is canvas state. */
export function isFrameHiddenByMetadata(
  frame: Pick<FrameData, "metadata"> | undefined,
): boolean {
  return !!frame?.metadata?.hidden;
}
